import _ from "lodash";

import templates from "../data/template";
import propertyPanelDefaultBaselines from "../data/propertyPanelDefaultBaselines.json";
import { PROPERTY_PANEL_REGISTRY } from "../data/propertyPanelRegistry";
import { isAmbifi3dAuthoringEnabled } from "./featureFlags";

export const PROPERTY_PANEL_EDITOR_CLASS = "ambifi-property-panel-editor";
export const PROPERTY_PANEL_EXPANSION_EVENT = "ambifi:property-panel-expansion";
export const PROPERTY_PANEL_FILTER_EVENT = "ambifi:property-panel-filter";
export const PROPERTY_PANEL_FILTER_STATE_KEY = "__ambifiPropertyPanelFilterState";

export function getPropertyPanelNodeContextKey(node = {}) {
	const safeNode = node || {};
	const entity = _.get(safeNode, "entity", {});
	const identity = safeNode.id || entity.guid || entity.entityId || entity.id || "";
	return `${identity};${entity.type || ""}`;
}

export const PROPERTY_PANEL_GROUPS = [
	{
		id: "content",
		label: "Content",
		panelKeys: [
			"activity information",
			"title",
			"title advanced",
			"label 1",
			"label 1 advanced",
			"label 2",
			"label 2 advanced",
			"comments",
			"description",
			"visibility",
			"completion status",
			"breadcrumb bar",
			"presentation mode",
			"default text alignment",
			"text alignment",
			"container header"
		]
	},
	{
		id: "media",
		label: "Media",
		panelKeys: [
			"media",
			"media upload options",
			"media-upload-options",
			"itemLayout",
			"item layout",
			"mediaControls",
			"media controls",
			"ambifi3d",
			"3d split screen",
			"ambifi 3d",
			"videoOptions",
			"video options",
			"imageOptions",
			"image options",
			"pdf options",
			"title card image",
			"logo image",
			"logo image for normal large screens",
			"logo image for small screens",
			"background audio",
			"label 1 audio file",
			"label 2 audio file",
			"comments audio file"
		]
	},
	{
		id: "layout",
		label: "Layout",
		panelKeys: [
			"layout",
			"basic options",
			"item tools",
			"containerLayout",
			"container layout",
			"choiceLayout",
			"choice layout",
			"choice items",
			"single choice itens",
			"multi choice items",
			"labels overlay",
			"keyboard settings",
			"text input options",
			"yes no options",
			"date picker options",
			"barcode scanner options",
			"sketchpad options",
			"ranking options",
			"picker options",
			"preferences",
			"submit button"
		]
	},
	{
		id: "styling",
		label: "Styling",
		panelKeys: [
			"styles",
			"styling",
			"theme",
			"stylesheet",
			"classes",
			"fonts"
		]
	},
	{
		id: "logic",
		label: "Logic",
		panelKeys: [
			"valueTrigger",
			"value trigger",
			"aiAction",
			"ai action",
			"functions"
		]
	},
	{
		id: "linking",
		label: "Linking",
		panelKeys: [
			"link",
			"linking options"
		]
	},
	{
		id: "navigation",
		label: "Navigation",
		panelKeys: [
			"layoutAdvanced",
			"item navigation",
			"navigation-title",
			"navigation title"
		]
	},
	{
		id: "advanced",
		label: "Advanced",
		panelKeys: [
			"advanced",
			"advanced options",
			"authorProperties",
			"author properties",
			"participant-completion",
			"participant completion",
			"id",
			"id and groups",
			"audio options",
			"timers",
			"tracking",
			"on complete",
			"icon",
			"print options",
			"drive root path",
			"general",
			"workflow",
			"filters",
			"drivesFilters",
			"filter selector",
			"showFor",
			"show for",
			"activity behavior",
			"activity automation",
			"activity automation json",
			"preview params",
			"activity summary panels actions",
			"related items",
			"attached data sources",
			"content model references",
			"split screen pricing content"
		]
	}
];

const EXCLUDED_REPORT_KEYS = new Set([
	"checked",
	"children",
	"completionState",
	"contributors",
	"entityId",
	"externalResources",
	"guid",
	"horzScrollPos",
	"identityId",
	"label1EditorAdvanced",
	"label2EditorAdvanced",
	"refresh",
	"revision",
	"revisionTs",
	"selected",
	"selectedListIndex",
	"trackData",
	"type",
	"vertScrollPos",
	"workflowStatesDs"
]);

const EXCLUDED_REPORT_KEYS_BY_SCOPE = {
	activity: new Set([
		"checklistId",
		"guid",
		"id",
		"revision",
		"revisionTs"
	])
};

const REPORT_DEFAULT_OVERRIDES_BY_SCOPE = {
	list: {
		iconType: "none"
	},
	item: {
		pickerVisualImageFit: "cover",
		requiredMessage: "A value is required.",
		textInputCurrencySeparator: ","
	}
};

const REPORT_HIERARCHICAL_ITEM_MEDIA_PRESENTATION_DEFAULTS = Object.freeze({
	itemHeight: "default",
	videoMute: true,
	videoLoop: true,
	videoAspectRatio: "",
	videoFitAvailableSpace: false,
	videoMaximumHeight: "default",
	videoMaximumHeightAnchor: "top",
	videoShowInBackground: false,
	videoResizeMode: "contain",
	videoBackgroundColor: "transparent",
	videoOverlayColor: "transparent",
	videoOverlayColorCustom: "rgba(255,255,255,0.7)",
	videoInlineViewer: "native",
	videoModalViewer: "native",
	videoStartTime: "",
	videoEndTime: "",
	videoLoopSegment: false,
	videoResumeBehavior: "restart",
	videoCompletionBehavior: "default",
	videoActionVisibility: "visible",
	videoCaption: "",
	imageAspectRatio: "16:9",
	imageAspectRatioCustom: "",
	imageMaximumHeight: "default",
	imageMaximumHeightAnchor: "top",
	imageShowInBackground: false,
	imageFitAvailableSpace: false,
	backgroundMediaBlurFill: false,
	imageResizeMode: "contain",
	imageOverlayColor: "transparent",
	imageOverlayColorCustom: "rgba(255,255,255,0.7)",
	imageInlineViewer: "native",
	imageModalViewer: "native",
	imageCropMode: "focusZoom",
	imageFocalPoint: "center",
	imageCropZoom: "100%",
	imageCropRect: "0% 0% 100% 100%",
	imageModalInitialZoom: "default",
	imageActionVisibility: "visible",
	imageCaption: "",
	imageAltText: "",
	pdfInlineViewer: "inherit",
	pdfModalViewer: "inherit",
	pdfFitAvailableSpace: true,
	pdfViewMode: "inherit",
	pdfSinglePage: "inherit",
	pdfInitialPage: "",
	pdfInitialZoom: "default",
	pdfPageMode: "none",
	pdfNamedDestination: "",
	pdfSearchText: ""
});

const REPORT_HIERARCHICAL_ITEM_MEDIA_PRESENTATION_KEYS = new Set(Object.keys(REPORT_HIERARCHICAL_ITEM_MEDIA_PRESENTATION_DEFAULTS));

const DEFAULTISH_REPEATABLE_NUMBER_KEYS = new Set([
	"repeatableIndex",
	"repeatableInitialCount",
	"repeatableMaxCount"
]);

const PANEL_LABEL_BY_ANCHOR = {
	"activity-information": "Activity Information",
	"activity-automation": "Activity Automation",
	"activity-automation-json": "Activity Automation JSON",
	"activity-summary-panels-actions": "Activity Summary Panels & Actions",
	"background-audio": "Background Audio",
	"breadcrumb-bar": "Breadcrumb Bar",
	"title": "Title",
	"description": "Description",
	"label-1": "Label 1",
	"label-2": "Label 2",
	"comments": "Comments",
	"link": "Link",
	"linking-options": "Linking Options",
	"navigation-title": "Navigation Title",
	"visibility": "Visibility",
	"completion-status": "Completion Status",
	"layout": "Basic Options",
	"media-upload-options": "Media Upload Options",
	"itemLayout": "Media",
	"mediaControls": "Media Controls",
	"ambifi3d": "3D Split Screen",
	"videoOptions": "Video Options",
	"imageOptions": "Image Options",
	"pdf-options": "PDF Options",
	"title-card-image": "Title Card Image",
	"styles": "Styling",
	"layoutAdvanced": "Item Navigation",
	"choiceLayout": "Choice Layout",
	"containerLayout": "Container Layout",
	"keyboard-settings": "Keyboard Settings",
	"text-input-options": "Text Input Options",
	"drivesFilters": "Filter Selector",
	"filters": "Filters",
	"show-for": "Filters",
	"relatedItems": "Related Items",
	"valueTrigger": "Value Trigger",
	"aiAction": "AI Action",
	"functions": "Functions",
	"timers": "Timers",
	"tracking": "Tracking",
	"on-complete": "On Complete",
	"print-options": "Print Options",
	"audio-options": "Audio Options",
	"activity-behavior": "Activity Behavior",
	"authorProperties": "Author Properties",
	"participant-completion": "Participant Completion",
	"preview-params": "Preview Params",
	"id": "ID & Groups",
	"advanced-options": "Advanced Options",
	"attached-data-sources": "Attached Data Sources",
	"content-model-references": "Content Model References",
	"drive-root-path": "Drive Root Path",
	"general": "General",
	"logo-image-for-normal-large-screens": "Logo Image (For normal/large screens)",
	"logo-image-for-small-screens": "Logo Image (For small screens)",
	"workflow": "Workflow"
};

const EXPLICIT_MEDIA_PANEL_ANCHOR_BY_PROPERTY = {
	titlecardimage: "title-card-image",
	heroimage: "title-card-image",
	heroimagedrive: "title-card-image",
	logoimagelarge: "logo-image-for-normal-large-screens",
	logoimage: "logo-image-for-normal-large-screens",
	logoimagedrive: "logo-image-for-normal-large-screens",
	logoimagealign: "logo-image-for-normal-large-screens",
	logoimagesmall: "logo-image-for-small-screens",
	logoimagesmalldrive: "logo-image-for-small-screens",
	logoimagesmallalign: "logo-image-for-small-screens",
	backgroundaudio: "background-audio",
	backgroundaudiodrive: "background-audio",
	backgroundaudiovolume: "background-audio"
};

const ITEM_TYPES_WITH_CHECK_LINK = [
	"item",
	"itemTextInput",
	"itemTextInputMultiline",
	"itemChoiceSingle",
	"itemChoiceMulti",
	"itemRanking",
	"itemDate",
	"itemTime",
	"itemDateTime",
	"itemAddress",
	"itemLocation",
	"itemImagePicker",
	"itemBarcodeScanner",
	"itemSketchPad"
];

const ITEM_PROPERTY_CONTEXTS = [
	{
		id: "checkLink",
		entityTypes: ITEM_TYPES_WITH_CHECK_LINK,
		propertyNames: ["linkOnCheck", "linkActionType", "linkId"],
		panelAnchors: ["link"]
	},
	{
		id: "choiceCommon",
		entityTypes: ["itemChoiceSingle", "itemChoiceMulti"],
		propertyPrefixes: ["choice"],
		excludedPropertyNames: ["choiceAdvanceOnSelect"],
		excludedPropertyPrefixes: ["choiceLink"],
		panelAnchors: ["choiceLayout", "single-choice-itens", "multi-choice-items"]
	},
	{
		id: "choiceSingle",
		entityTypes: ["itemChoiceSingle"],
		propertyNames: ["choiceAdvanceOnSelect"],
		propertyPrefixes: ["choiceLink"],
		panelAnchors: ["linking-options"]
	},
	{
		id: "picker",
		entityTypes: ["itemPicker"],
		propertyPrefixes: ["picker"],
		panelAnchors: ["linking-options", "picker-options"]
	},
	{
		id: "yesNo",
		entityTypes: ["itemYesNo"],
		propertyPrefixes: ["yesNo"],
		panelAnchors: ["link", "yes-no-options"]
	},
	{
		id: "textInput",
		entityTypes: ["itemTextInput", "itemTextInputMultiline"],
		propertyPrefixes: ["textInput", "keyboard"],
		panelAnchors: ["text-input-options", "keyboard-settings"]
	},
	{
		id: "dateTime",
		entityTypes: ["itemDate", "itemTime", "itemDateTime"],
		propertyPrefixes: ["date", "time", "dateTime"],
		panelAnchors: ["date-picker-options"]
	},
	{
		id: "ranking",
		entityTypes: ["itemRanking"],
		propertyPrefixes: ["ranking"],
		panelAnchors: ["ranking-options"]
	},
	{
		id: "mediaUpload",
		entityTypes: ["itemImagePicker"],
		propertyPrefixes: ["mediaAdd", "mediaMax", "mediaType"],
		panelAnchors: ["media-upload-options"]
	},
	{
		id: "barcode",
		entityTypes: ["itemBarcodeScanner"],
		propertyPrefixes: ["barcode"],
		panelAnchors: ["barcode-scanner-options"]
	},
	{
		id: "sketch",
		entityTypes: ["itemSketchPad"],
		propertyPrefixes: ["sketch"],
		panelAnchors: ["sketchpad-options"]
	}
];

const ITEM_PROPERTY_CONTEXT_BY_ENTITY_TYPE = ITEM_PROPERTY_CONTEXTS.reduce((acc, context) => {
	context.entityTypes.forEach((entityType) => {
		if (!acc[entityType]) {
			acc[entityType] = [];
		}
		acc[entityType].push(context.id);
	});
	return acc;
}, {});

export function normalizePanelKey(value) {
	return _.trim(_.toString(value || ""))
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.toLowerCase()
		.replace(/&/g, " and ")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

export function getAllPropertyPanelGroupIds() {
	return PROPERTY_PANEL_GROUPS.map((group) => group.id);
}

export function getPropertyPanelScope(entity = {}) {
	const type = _.toString(_.get(entity, "type", ""));
	if (type === "checklist") {
		return "activity";
	}
	if (type === "list") {
		return "list";
	}
	if (type === "section") {
		return "section";
	}
	return "item";
}

function normalizeRegistryPropertyKey(value = "") {
	return _.toString(value || "").toLowerCase();
}

function registryListContains(values = [], key = "") {
	const normalizedKey = normalizeRegistryPropertyKey(key);
	return (values || []).some((value) => normalizeRegistryPropertyKey(value) === normalizedKey);
}

function isPropertyPanelFeatureEnabled(panel = {}, urlParams = null, selectedOrg = null) {
	switch (panel.featureGate) {
		case "ambifi3dAuthoring": {
			return isAmbifi3dAuthoringEnabled(urlParams, selectedOrg);
		}
		default: {
			return true;
		}
	}
}

function getEnabledPropertyPanelRegistry(urlParams = null, selectedOrg = null) {
	return PROPERTY_PANEL_REGISTRY.filter((panel) => isPropertyPanelFeatureEnabled(panel, urlParams, selectedOrg));
}

function getPropertyPanelRegistryMatch(panelKey = "") {
	const normalizedPanelKey = normalizePanelKey(panelKey);
	if (!normalizedPanelKey) {
		return null;
	}
	return PROPERTY_PANEL_REGISTRY.find((panel) => (
		normalizePanelKey(panel.anchor) === normalizedPanelKey ||
		normalizePanelKey(panel.title) === normalizedPanelKey ||
		(panel.aliases || []).some((alias) => normalizePanelKey(alias) === normalizedPanelKey) ||
		(panel.properties || []).some((property) => normalizePanelKey(property) === normalizedPanelKey) ||
		(panel.contentKeys || []).some((contentKey) => normalizePanelKey(contentKey) === normalizedPanelKey)
	)) || null;
}

export function getPropertyPanelMetadataForAnchor(anchor = "", options = {}) {
	const normalizedAnchor = normalizePanelKey(anchor);
	if (!normalizedAnchor) {
		return null;
	}
	return getEnabledPropertyPanelRegistry(options.urlParams, options.selectedOrg).find((panel) => normalizePanelKey(panel.anchor) === normalizedAnchor) || null;
}

function getPropertyPanelMetadataMatchScore(panel = {}, entity = {}, matchKind = "property") {
	const entityType = _.toString(_.get(entity, "type", ""));
	const hasEntityType = !!entityType;
	const scope = hasEntityType ? getPropertyPanelScope(entity) : "";
	const panelScopes = panel.scopes || [];
	const panelEntityTypes = panel.entityTypes || [];

	if (scope && panelScopes.length > 0 && !panelScopes.includes(scope)) {
		return -Infinity;
	}
	if (hasEntityType && panelEntityTypes.length > 0 && !panelEntityTypes.includes(entityType)) {
		return -Infinity;
	}

	let score = matchKind === "property" ? 1000 : 900;
	if (scope && panelScopes.length > 0) {
		score += 20 - panelScopes.length;
	}
	if (hasEntityType && panelEntityTypes.includes(entityType)) {
		score += 100;
	} else if (!hasEntityType && panelEntityTypes.length > 0) {
		score -= 20;
	}
	return score;
}

export function getPropertyPanelMetadataForProperty(propName = "", entity = {}, options = {}) {
	const includeContentKeys = options.includeContentKeys !== false;
	const normalizedPropName = normalizeRegistryPropertyKey(propName);
	if (!normalizedPropName) {
		return null;
	}

	const candidates = [];
	getEnabledPropertyPanelRegistry(options.urlParams, options.selectedOrg).forEach((panel) => {
		if (registryListContains(panel.properties, normalizedPropName)) {
			const score = getPropertyPanelMetadataMatchScore(panel, entity, "property");
			if (score !== -Infinity) {
				candidates.push({ panel, score });
			}
		}
		if (includeContentKeys && registryListContains(panel.contentKeys, normalizedPropName)) {
			const score = getPropertyPanelMetadataMatchScore(panel, entity, "contentKey");
			if (score !== -Infinity) {
				candidates.push({ panel, score });
			}
		}
	});

	if (candidates.length === 0) {
		return null;
	}

	return _.orderBy(candidates, [(candidate) => candidate.score, (candidate) => candidate.panel.title], ["desc", "asc"])[0].panel;
}

export function getPropertyPanelContentKeyForProperty(propName = "", contentProperties = {}, entity = {}) {
	if (Object.prototype.hasOwnProperty.call(contentProperties || {}, propName)) {
		return propName;
	}

	const metadata = getPropertyPanelMetadataForProperty(propName, entity);
	const registryContentKey = (metadata ? metadata.contentKeys || [] : []).find((contentKey) => (
		Object.prototype.hasOwnProperty.call(contentProperties || {}, contentKey)
	));
	if (registryContentKey) {
		return registryContentKey;
	}

	if ((propName === "label" || propName === "label1") && contentProperties.label1) {
		return "label1";
	}
	if ((propName === "label" || propName === "name") && contentProperties.title) {
		return "title";
	}
	return "";
}

export function getContentDocForProperty(propName = "", contentProperties = {}, entity = {}, fallbackDoc = {}) {
	const contentKey = getPropertyPanelContentKeyForProperty(propName, contentProperties, entity);
	return contentKey ? contentProperties[contentKey] : fallbackDoc;
}

export function buildPropertyPanelFilter(activeGroupIds = getAllPropertyPanelGroupIds()) {
	const active = new Set((activeGroupIds || []).filter(Boolean));
	const allGroupIds = getAllPropertyPanelGroupIds();
	const allGroupsActive = allGroupIds.every((id) => active.has(id));
	const panelGroupByKey = {};
	const panelGroupIdsByKey = {};
	const hiddenPanelKeys = {};

	PROPERTY_PANEL_GROUPS.forEach((group) => {
		(group.panelKeys || []).forEach((key) => {
			const normalizedKey = normalizePanelKey(key);
			if (!normalizedKey) {
				return;
			}
			if (!panelGroupByKey[normalizedKey]) {
				panelGroupByKey[normalizedKey] = group.id;
			}
			panelGroupIdsByKey[normalizedKey] = _.uniq((panelGroupIdsByKey[normalizedKey] || []).concat(group.id));
		});
	});

	Object.keys(panelGroupIdsByKey).forEach((key) => {
		if (!panelGroupIdsByKey[key].some((groupId) => active.has(groupId))) {
			hiddenPanelKeys[key] = true;
		}
	});

	return {
		activeGroupIds: Array.from(active),
		allGroupsActive,
		hiddenPanelKeys,
		panelGroupByKey,
		panelGroupIdsByKey
	};
}

function getGroupForPanelAnchor(anchor = "", fallbackLabel = "", options = {}) {
	const normalizedAnchor = normalizePanelKey(anchor);
	const normalizedLabel = normalizePanelKey(fallbackLabel);
	if (normalizedAnchor === "advanced-options" || normalizedLabel === "advanced-options") {
		return "advanced";
	}
	const metadata = getPropertyPanelMetadataForAnchor(anchor, options) || getPropertyPanelMetadataForAnchor(fallbackLabel, options);
	if (metadata && metadata.groupId) {
		return metadata.groupId;
	}
	const match = PROPERTY_PANEL_GROUPS.find((group) => (
		(group.panelKeys || []).some((key) => {
			const normalizedKey = normalizePanelKey(key);
			return normalizedKey && (normalizedKey === normalizedAnchor || normalizedKey === normalizedLabel);
		})
	));
	return match ? match.id : null;
}

function getGroupForProperty(propName = "", doc = {}) {
	const prop = _.toString(propName);
	const propLower = prop.toLowerCase();
	const searchable = `${prop} ${_.get(doc, "name", "")} ${_.get(doc, "description", "")}`.toLowerCase();

	if (prop === "id") {
		return "advanced";
	}
	if (
		propLower === "groupnames" ||
		propLower === "suppressprint" ||
		["suppressaudio", "suppressaudiocompletion", "suppresscompletionconfirmation", "debugmode", "autosaveoncheck", "pricingqtydisplaymode", "starttimer", "stoptimer", "startnamedtimers", "stopnamedtimers", "starttracking", "stoptracking", "saveoncheck", "saveandexitoncheck", "generatereportoncheck", "generatereportonchecktemplate"].includes(propLower)
	) {
		return "advanced";
	}
	if (
		propLower === "navtitle" ||
		propLower === "excludefromnavigation" ||
		/^itembutton/.test(propLower) ||
		/^fullscreen(action|swipe|labelhidecontrol)/.test(propLower) ||
		/navigation|navigate|nav title/.test(searchable)
	) {
		return "navigation";
	}
	if (/link/.test(searchable)) {
		return "linking";
	}
	if (/valuetrigger/.test(propLower) || /\bfunction|ai action|aiaction/.test(searchable)) {
		return "logic";
	}
	if (/related/.test(searchable)) {
		return "advanced";
	}
	if (/label|title|subtitle|comment|note|description|visible|enabled|completion|mandatory|breadcrumb/.test(searchable)) {
		return "content";
	}
	if (/media|image|video|audio|pdf|fullscreen control|resize toggle|replay|background/.test(searchable)) {
		return "media";
	}
	if (/style|class|color|theme|font|stylesheet/.test(searchable)) {
		return "styling";
	}
	if (/layout|view|button|tool|picker|choice|keyboard|barcode|ranking|submit|preference/.test(searchable)) {
		return "layout";
	}
	if (/filter|workflow|trigger|automation|datasource|data source|content model/.test(searchable)) {
		return "advanced";
	}
	return "advanced";
}

function getPrimaryPanelGroup(anchor = "", panelLabel = "", fallbackGroupId = "advanced") {
	return getGroupForPanelAnchor(anchor, panelLabel) || fallbackGroupId;
}

function getSearchPropertyGroup(propName = "", doc = {}, entity = {}, options = {}) {
	const metadata = getPropertyPanelMetadataForProperty(propName, entity, options);
	if (metadata && metadata.groupId) {
		return metadata.groupId;
	}
	const anchor = getAnchorForProperty(propName, doc, entity, options);
	return getGroupForPanelAnchor(anchor, getPanelLabelForAnchor(anchor, options), options) || getGroupForProperty(propName, doc);
}

export function getSearchPanelGroup(anchor = "", panelLabel = "", fallbackGroupId = "advanced") {
	return getPrimaryPanelGroup(anchor, panelLabel, fallbackGroupId);
}

function getCanonicalPanelAnchor(panelKey = "") {
	const normalizedPanelKey = normalizePanelKey(panelKey);
	if (!normalizedPanelKey) {
		return "";
	}

	const registryPanel = getEnabledPropertyPanelRegistry().find((panel) => (
		normalizePanelKey(panel.anchor) === normalizedPanelKey ||
		normalizePanelKey(panel.title) === normalizedPanelKey
	));
	if (registryPanel) {
		return registryPanel.anchor;
	}

	const knownAnchor = Object.keys(PANEL_LABEL_BY_ANCHOR).find((anchor) => (
		normalizePanelKey(anchor) === normalizedPanelKey ||
		normalizePanelKey(PANEL_LABEL_BY_ANCHOR[anchor]) === normalizedPanelKey
	));
	return knownAnchor || normalizedPanelKey;
}

function mergeContextRelevance(a = "generic", b = "generic") {
	if (a === "current" || b === "current") {
		return "current";
	}
	if (a === "generic" || b === "generic") {
		return "generic";
	}
	return "other";
}

function mergeSearchText(...values) {
	return _.uniq(values
		.reduce((result, value) => result.concat(_.isArray(value) ? value : [value]), [])
		.map((value) => _.trim(_.toString(value || "")))
		.filter(Boolean)
	).join(" ");
}

function mergePanelSearchEntry(existing, next) {
	if (!existing) {
		return next;
	}

	return {
		...existing,
		...next,
		groupId: existing.groupId || next.groupId,
		contextRelevance: mergeContextRelevance(existing.contextRelevance, next.contextRelevance),
		aliasText: mergeSearchText(existing.aliasText, next.aliasText),
		searchText: mergeSearchText(existing.searchText, next.searchText)
	};
}

function buildPanelTitleSearchEntries(entity = {}, options = {}) {
	const entriesByAnchor = {};

	PROPERTY_PANEL_GROUPS.forEach((group) => {
		(group.panelKeys || []).forEach((panelKey) => {
			const registryPanelForKey = getPropertyPanelRegistryMatch(panelKey);
			if (registryPanelForKey && !isPropertyPanelFeatureEnabled(registryPanelForKey, options.urlParams, options.selectedOrg)) {
				return;
			}

			const anchor = getCanonicalPanelAnchor(panelKey);
			if (!anchor) {
				return;
			}

			const panelLabel = getPanelLabelForAnchor(anchor, options);
			const entry = {
				type: "panel",
				anchor,
				groupId: getSearchPanelGroup(anchor, panelLabel, group.id),
				label: panelLabel,
				panelLabel,
				contextRelevance: getSearchContextRelevance(entity, "", anchor),
				labelText: panelLabel,
				propertyText: "",
				panelText: panelLabel,
				aliasText: mergeSearchText(panelKey, anchor, panelLabel),
				descriptionText: "",
				searchText: mergeSearchText(panelLabel, panelKey, anchor)
			};

			entriesByAnchor[anchor] = mergePanelSearchEntry(entriesByAnchor[anchor], entry);
		});
	});

		getEnabledPropertyPanelRegistry(options.urlParams, options.selectedOrg).forEach((panel) => {
		const anchor = panel.anchor;
		if (!anchor) {
			return;
		}

		const panelLabel = getPanelLabelForAnchor(anchor, options);
		const entry = {
			type: "panel",
			anchor,
			groupId: getSearchPanelGroup(anchor, panelLabel, panel.groupId),
			label: panelLabel,
			panelLabel,
			contextRelevance: getSearchContextRelevance(entity, "", anchor),
			labelText: panelLabel,
			propertyText: "",
			panelText: panelLabel,
			aliasText: mergeSearchText(panel.aliases, panel.properties, panel.contentKeys),
			descriptionText: "",
			searchText: mergeSearchText(panelLabel, anchor, panel.aliases, panel.properties, panel.contentKeys)
		};

		entriesByAnchor[anchor] = mergePanelSearchEntry(entriesByAnchor[anchor], entry);
	});

	return entriesByAnchor;
}

/*
 * Keep this behavior intentionally conservative: unknown panels only appear in
 * All. Category chips should show classified capability sets, not every panel
 * that happened to be unclassified.
 */
export function isPropertyPanelKeyVisibleForFilter(panelKeys = [], detail = {}) {
	const allGroupsActive = !!_.get(detail, "allGroupsActive", false);
	const hiddenPanelKeys = _.get(detail, "hiddenPanelKeys", {});
	const panelGroupIdsByKey = _.get(detail, "panelGroupIdsByKey", {});
	const normalizedPanelKeys = (panelKeys || []).map(normalizePanelKey).filter(Boolean);
	if (normalizedPanelKeys.length === 0) {
		return true;
	}
	if (allGroupsActive) {
		return true;
	}
	return normalizedPanelKeys.some((key) => panelGroupIdsByKey[key] && !hiddenPanelKeys[key]);
}

/*
 * Advanced Options is a structural wrapper in several editors. Let it stay
 * visible for categories with nested children; child panels still do the actual
 * category filtering.
 */
PROPERTY_PANEL_GROUPS.forEach((group) => {
	if (["media", "logic", "linking", "navigation", "advanced"].includes(group.id)) {
		group.panelKeys.push("advanced options");
	}
});

function getPropertySearchAliases(propName = "", doc = {}, entity = {}) {
	const prop = _.toString(propName);
	const label = _.toString(_.get(doc, "name", ""));
	const aliases = [];
	const metadata = getPropertyPanelMetadataForProperty(propName, entity);

	if (metadata) {
		aliases.push(...(metadata.aliases || []), ...(metadata.contentKeys || []));
	}

	if (prop === "visibleIf" || prop === "conditionalVisible" || /visible if/i.test(label)) {
		aliases.push(
			"conditionalVisible",
			"conditional visible",
			"conditional visibility",
			"visible if",
			"show if",
			"hide if",
			"condition",
			"rules"
		);
	}

	if (prop === "visible" || /^visible$/i.test(label)) {
		aliases.push("visibility", "show", "hide");
	}

	if (/visibility/i.test(prop) || /visibility/i.test(label)) {
		aliases.push("visible", "show", "hide");
	}

	return aliases.join(" ");
}

function splitPropertyLabel(rawLabel = "") {
	const label = _.toString(rawLabel || "");
	const match = label.match(/^(.+?)\s*\((.{24,})\)\s*$/);
	if (!match) {
		return {
			label,
			helperText: ""
		};
	}
	return {
		label: _.trim(match[1]),
		helperText: _.trim(match[2])
	};
}

export function getPropertyLabelTargetAnchorForLabel(label = "") {
	const labelKey = normalizePanelKey(label);
	return labelKey ? `property-label-${labelKey}` : "";
}

export function getPropertyTargetAnchor(propName = "", doc = {}) {
	const rawLabel = _.get(doc, "name", propName);
	const splitLabel = splitPropertyLabel(rawLabel);
	return getPropertyLabelTargetAnchorForLabel(splitLabel.label || propName);
}

function getExplicitMediaPanelAnchorForProperty(propLower = "", searchable = "") {
	if (EXPLICIT_MEDIA_PANEL_ANCHOR_BY_PROPERTY[propLower]) {
		return EXPLICIT_MEDIA_PANEL_ANCHOR_BY_PROPERTY[propLower];
	}

	if (/title card image/.test(searchable)) {
		return "title-card-image";
	}
	if (/logo image/.test(searchable) || /small logo image/.test(searchable)) {
		return /small/.test(searchable)
			? "logo-image-for-small-screens"
			: "logo-image-for-normal-large-screens";
	}
	if (/background audio/.test(searchable)) {
		return "background-audio";
	}

	return "";
}

function contextMatchesProperty(context, propName) {
	const prop = _.toString(propName || "");
	if (!prop) {
		return false;
	}
	if ((context.excludedPropertyNames || []).includes(prop)) {
		return false;
	}
	if ((context.excludedPropertyPrefixes || []).some((prefix) => prop.indexOf(prefix) === 0)) {
		return false;
	}
	if ((context.propertyNames || []).includes(prop)) {
		return true;
	}
	return (context.propertyPrefixes || []).some((prefix) => prop.indexOf(prefix) === 0);
}

function getItemPropertyContextIds(propName = "", anchor = "") {
	const prop = _.toString(propName || "");
	const normalizedAnchor = normalizePanelKey(anchor);

	if (prop) {
		return ITEM_PROPERTY_CONTEXTS
			.filter((candidate) => contextMatchesProperty(candidate, prop))
			.map((candidate) => candidate.id);
	}

	return ITEM_PROPERTY_CONTEXTS.filter((candidate) => (
		candidate.panelAnchors.some((panelAnchor) => normalizePanelKey(panelAnchor) === normalizedAnchor)
	)).map((candidate) => candidate.id);
}

function getSearchContextRelevance(entity = {}, propName = "", anchor = "") {
	const entityType = _.toString(_.get(entity, "type", ""));
	if (getPropertyPanelScope(entity) !== "item" || !entityType) {
		return "generic";
	}

	const selectedContextIds = new Set(ITEM_PROPERTY_CONTEXT_BY_ENTITY_TYPE[entityType] || []);
	const propertyContextIds = getItemPropertyContextIds(propName, anchor);

	if (propertyContextIds.length === 0) {
		return "generic";
	}
	return propertyContextIds.some((contextId) => selectedContextIds.has(contextId)) ? "current" : "other";
}

export function getAnchorForProperty(propName = "", doc = {}, entity = {}, options = {}) {
	const prop = _.toString(propName);
	const propLower = prop.toLowerCase();
	const searchable = `${prop} ${_.get(doc, "name", "")}`.toLowerCase();
	const metadata = getPropertyPanelMetadataForProperty(propName, entity, options);
	const explicitMediaPanelAnchor = getExplicitMediaPanelAnchorForProperty(propLower, searchable);

	if (prop === "id") {
		return "id";
	}
	if (metadata && metadata.anchor) {
		return metadata.anchor;
	}
	if (explicitMediaPanelAnchor) {
		return explicitMediaPanelAnchor;
	}
	if (propLower === "groupnames") {
		return "id";
	}
	if (propLower === "suppressprint") {
		return "print-options";
	}
	if (["suppressaudio", "suppressaudiocompletion", "suppresscompletionconfirmation"].includes(propLower)) {
		return "audio-options";
	}
	if (["debugmode", "autosaveoncheck", "pricingqtydisplaymode"].includes(propLower)) {
		return "activity-behavior";
	}
	if (["starttimer", "stoptimer", "startnamedtimers", "stopnamedtimers"].includes(propLower)) {
		return "timers";
	}
	if (["starttracking", "stoptracking"].includes(propLower)) {
		return "tracking";
	}
	if (["saveoncheck", "saveandexitoncheck", "generatereportoncheck", "generatereportonchecktemplate"].includes(propLower)) {
		return "on-complete";
	}
	if (propLower === "navtitle" || propLower === "excludefromnavigation") {
		return "navigation-title";
	}
	if (prop === "label") {
		return /^label 1$/i.test(_.get(doc, "name", "")) ? "label-1" : "title";
	}
	if (/^label1|label 1/.test(searchable)) {
		return "label-1";
	}
	if (/^label2|label 2/.test(searchable)) {
		return "label-2";
	}
	if (/title|subtitle|namealexa|nameaudio|namessml|nameprint/.test(searchable)) {
		return "title";
	}
	if (/description/.test(searchable)) {
		return "description";
	}
	if (/comment|note/.test(searchable)) {
		return "comments";
	}
	if (/^choice.*link/.test(propLower) || /^picker.*link/.test(propLower)) {
		return "linking-options";
	}
	if (/^yesno.*link/.test(propLower) || propLower === "linkoncheck" || propLower === "linkactiontype" || propLower === "linkid") {
		return "link";
	}
	if (/enabled|visible|conditional|searchable/.test(searchable)) {
		return "visibility";
	}
	if (/completion|mandatory|ignorecompletion/.test(searchable)) {
		return "completion-status";
	}
	if (/^media(add|max|type)/.test(propLower) || /^imagepicker/.test(propLower)) {
		return "media-upload-options";
	}
	if (/mediafullscreen|mediaresize|mediareplay/.test(propLower)) {
		return "mediaControls";
	}
	if (/ambifi3d|3d split screen|3dsplitscreen|split.?screen.?3d|3d model|3d camera|3d texture|3dmodel|3dcamera|3dtexture/.test(searchable)) {
		return "ambifi3d";
	}
	if (/video/.test(searchable)) {
		return "videoOptions";
	}
	if (/image|hero|logo|backgroundimage/.test(searchable)) {
		return "imageOptions";
	}
	if (/audio|pdf|media|threeD|3d/.test(searchable)) {
		return "itemLayout";
	}
	if (/style|class|color|theme|font|stylesheet/.test(searchable)) {
		return "styles";
	}
	if (/itembutton|fullscreen|swipe|navigation|nav/.test(searchable)) {
		return "layoutAdvanced";
	}
	if (/^keyboard/.test(propLower) || /^textinputkeyboard/.test(propLower)) {
		return "keyboard-settings";
	}
	if (/^textinput/.test(propLower)) {
		return "text-input-options";
	}
	if (/choice/.test(searchable)) {
		return "choiceLayout";
	}
	if (/container/.test(searchable)) {
		return "containerLayout";
	}
	if (propLower === "drivesfilters") {
		return "drivesFilters";
	}
	if (/filter|showfor|filtertags/.test(searchable)) {
		return "filters";
	}
	if (/related/.test(searchable)) {
		return "relatedItems";
	}
	if (/valuetrigger/.test(propLower)) {
		return "valueTrigger";
	}
	if (/ai/.test(searchable)) {
		return "aiAction";
	}
	if (/function/.test(searchable)) {
		return "functions";
	}
	if (/authorproperties/.test(propLower)) {
		return "authorProperties";
	}
	if (/identifier/.test(searchable)) {
		return "id";
	}
	return getGroupForProperty(prop, doc) === "layout" ? "layout" : "advanced-options";
}

export function getPanelLabelForAnchor(anchor, options = {}) {
	const metadata = getPropertyPanelMetadataForAnchor(anchor, options);
	if (metadata && metadata.title) {
		return metadata.title;
	}
	return PANEL_LABEL_BY_ANCHOR[anchor] || _.startCase(anchor);
}

export function buildPropertySearchEntries(contentProperties = {}, entity = {}, options = {}) {
	const panelEntriesByAnchor = buildPanelTitleSearchEntries(entity, options);
	const propertyEntries = Object.keys(contentProperties || {}).map((propName) => {
		const doc = contentProperties[propName] || {};
		const anchor = getAnchorForProperty(propName, doc, entity, options);
		const groupId = getSearchPropertyGroup(propName, doc, entity, options);
		const panelLabel = getPanelLabelForAnchor(anchor, options);
		const rawLabel = _.get(doc, "name", propName);
		const splitLabel = splitPropertyLabel(rawLabel);
		const label = splitLabel.label || propName;
		const targetAnchor = getPropertyTargetAnchor(propName, doc);
		const aliases = getPropertySearchAliases(propName, doc, entity);
		const description = _.get(doc, "description", "");
		const descriptionText = _.trim(`${description} ${splitLabel.helperText}`);
		const contextRelevance = getSearchContextRelevance(entity, propName, anchor);
		const panelGroupId = getSearchPanelGroup(anchor, panelLabel, groupId);

		panelEntriesByAnchor[anchor] = mergePanelSearchEntry(panelEntriesByAnchor[anchor], {
			type: "panel",
			anchor,
			groupId: panelGroupId,
			label: panelLabel,
			panelLabel,
			contextRelevance: getSearchContextRelevance(entity, "", anchor),
			labelText: panelLabel,
			propertyText: anchor,
			panelText: panelLabel,
			aliasText: "",
			descriptionText: "",
			searchText: `${panelLabel} ${anchor}`
		});

		return {
			type: "property",
			property: propName,
			anchor,
			targetAnchor,
			groupId,
			label,
			panelLabel,
			description,
			contextRelevance,
			labelText: label,
			propertyText: propName,
			panelText: panelLabel,
			aliasText: aliases,
			descriptionText,
			searchText: `${propName} ${label} ${aliases} ${descriptionText} ${panelLabel}`
		};
	});

	return _.sortBy(Object.values(panelEntriesByAnchor).concat(propertyEntries), [(entry) => entry.panelLabel, (entry) => entry.label]);
}

function getDefaultEntityForScope(scope, entity = {}) {
	const templateChecklist = _.get(templates, "template.templateChecklist", {});
	let templateDefault;
	let modernDefault;

	if (scope === "activity") {
		templateDefault = _.omit(_.get(templates, "template.blank", {}), ["children"]);
		modernDefault = _.get(propertyPanelDefaultBaselines, "activity", {});
	} else if (scope === "list") {
		templateDefault = _.omit(_.get(templateChecklist, "children[0]", {}), ["children"]);
		modernDefault = _.get(propertyPanelDefaultBaselines, "list", {});
	} else if (scope === "section") {
		templateDefault = _.omit(_.get(templateChecklist, "children[0].children[0]", {}), ["children"]);
		modernDefault = _.get(propertyPanelDefaultBaselines, "section", {});
	} else {
		const entityType = _.get(entity, "type", "item");
		const templateItem = _.omit(_.get(templateChecklist, "children[0].children[0].children[0]", {}), ["children"]);
		templateDefault = {
			...templateItem,
			type: entityType || _.get(templateItem, "type", "item")
		};
		modernDefault = entityType === "item" && _.get(entity, "labelOnly") === true
			? _.get(propertyPanelDefaultBaselines, "labelOnlyItem", {})
			: _.get(propertyPanelDefaultBaselines, `itemsByType.${entityType}`, _.get(propertyPanelDefaultBaselines, "itemsByType.item", {}));
	}

	return {
		...templateDefault,
		...modernDefault,
		..._.get(REPORT_DEFAULT_OVERRIDES_BY_SCOPE, scope, {}),
		...(scope === "item" && !_.has(modernDefault, "label") && _.has(modernDefault, "label1") ? { label: modernDefault.label1 } : {})
	};
}

function safeParseJsonString(value) {
	if (!_.isString(value)) {
		return value;
	}
	const trimmed = value.trim();
	if (!trimmed || !/^[{[]/.test(trimmed)) {
		return value;
	}
	try {
		return JSON.parse(trimmed);
	} catch (err) {
		return value;
	}
}

function normalizeComparableValue(value) {
	if (_.isString(value)) {
		const parsed = safeParseJsonString(value);
		if (parsed !== value) {
			return normalizeComparableValue(parsed);
		}
		return value.trim();
	}
	if (_.isArray(value)) {
		return value.map(normalizeComparableValue);
	}
	if (_.isPlainObject(value)) {
		return _.mapValues(value, normalizeComparableValue);
	}
	return value;
}

function isEmptySetValue(value) {
	const normalized = normalizeComparableValue(value);
	if (_.isUndefined(normalized) || normalized === null) {
		return true;
	}
	if (_.isString(normalized)) {
		return normalized === "" || normalized === "inherit" || normalized === "default";
	}
	if (_.isArray(normalized)) {
		return normalized.length === 0;
	}
	if (_.isPlainObject(normalized)) {
		return Object.keys(normalized).length === 0;
	}
	if (_.isBoolean(normalized)) {
		return normalized === false;
	}
	return normalized === 0;
}

function isDefaultishStringValue(value) {
	const normalized = normalizeComparableValue(value);
	return _.isString(normalized) && (normalized === "" || normalized === "inherit" || normalized === "default");
}

function isDefaultishImageAspectRatioValue(value) {
	const normalized = normalizeComparableValue(value);
	return _.isString(normalized) && (normalized === "" || normalized === "inherit" || normalized === "default" || normalized === "16:9");
}

function isDefaultishRepeatableNumberValue(key, value) {
	const normalized = normalizeComparableValue(value);
	if (key === "repeatableMaxCount") {
		return normalized === "" || normalized === 0 || normalized === "0";
	}
	return normalized === "" || normalized === 0 || normalized === 1 || normalized === "0" || normalized === "1";
}

function isInheritedMediaReportDefaultEquivalent(scope, key, value, defaultValue) {
	if (scope === "activity" || !REPORT_HIERARCHICAL_ITEM_MEDIA_PRESENTATION_KEYS.has(key)) {
		return false;
	}

	const normalizedValue = normalizeComparableValue(value);
	const normalizedDefault = normalizeComparableValue(defaultValue);
	const normalizedMediaDefault = normalizeComparableValue(REPORT_HIERARCHICAL_ITEM_MEDIA_PRESENTATION_DEFAULTS[key]);

	return (
		isDefaultishStringValue(normalizedDefault) && _.isEqual(normalizedValue, normalizedMediaDefault)
	) || (
		isDefaultishStringValue(normalizedValue) && _.isEqual(normalizedDefault, normalizedMediaDefault)
	);
}

function formatReportValue(value) {
	const normalized = safeParseJsonString(value);
	if (_.isString(normalized)) {
		return normalized;
	}
	if (_.isPlainObject(normalized) || _.isArray(normalized)) {
		return JSON.stringify(normalized);
	}
	return _.toString(normalized);
}

function isReportKeyExcluded(scope, key) {
	return EXCLUDED_REPORT_KEYS.has(key) || !!_.get(EXCLUDED_REPORT_KEYS_BY_SCOPE, scope, new Set()).has(key);
}

function getReportPropertyDoc(key, contentProperties = {}, entity = {}) {
	return getContentDocForProperty(key, contentProperties, entity, {});
}

function isReportPropertyRelevant(entity = {}, key = "", anchor = "") {
	return getPropertyPanelScope(entity) !== "item" || getSearchContextRelevance(entity, key, anchor) !== "other";
}

function isDuplicateLegacyReportAlias(entity = {}, key = "") {
	return key === "label" &&
		Object.prototype.hasOwnProperty.call(entity, "label1") &&
		_.isEqual(normalizeComparableValue(entity.label), normalizeComparableValue(entity.label1));
}

function isForcedSetContentReportValue(scope, key, value) {
	return scope === "item" &&
		(key === "label" || key === "label1") &&
		!isEmptySetValue(value);
}

function areReportValuesEquivalent(scope, key, value, defaultValue, hasDefault) {
	const normalizedValue = normalizeComparableValue(value);
	const normalizedDefault = normalizeComparableValue(defaultValue);

	if (hasDefault && _.isEqual(normalizedValue, normalizedDefault)) {
		return true;
	}
	if (hasDefault && isDefaultishStringValue(normalizedValue) && isDefaultishStringValue(normalizedDefault)) {
		return true;
	}
	if (key === "imageAspectRatio" && isDefaultishImageAspectRatioValue(normalizedValue) && isDefaultishImageAspectRatioValue(normalizedDefault)) {
		return true;
	}
	if (hasDefault && isInheritedMediaReportDefaultEquivalent(scope, key, normalizedValue, normalizedDefault)) {
		return true;
	}
	if (DEFAULTISH_REPEATABLE_NUMBER_KEYS.has(key) && isDefaultishRepeatableNumberValue(key, normalizedValue) && isDefaultishRepeatableNumberValue(key, normalizedDefault)) {
		return true;
	}
	if (!hasDefault && isEmptySetValue(value)) {
		return true;
	}
	if (
		key === "textInputCurrencySeparator" &&
		normalizedDefault === "," &&
		(normalizedValue === "" || _.isUndefined(normalizedValue) || normalizedValue === null)
	) {
		return true;
	}
	return false;
}

export function buildSetPropertyReport(entity = {}, contentProperties = {}) {
	const scope = getPropertyPanelScope(entity);
	const defaultEntity = getDefaultEntityForScope(scope, entity);

	return Object.keys(entity || {})
		.filter((key) => !isReportKeyExcluded(scope, key))
		.filter((key) => !isDuplicateLegacyReportAlias(entity, key))
		.reduce((results, key) => {
			const value = entity[key];
			const defaultValue = defaultEntity[key];
			const hasDefault = Object.prototype.hasOwnProperty.call(defaultEntity, key);

			if (!isForcedSetContentReportValue(scope, key, value) && areReportValuesEquivalent(scope, key, value, defaultValue, hasDefault)) {
				return results;
			}

			const doc = getReportPropertyDoc(key, contentProperties, entity);
			const anchor = getAnchorForProperty(key, doc, entity);
			if (!isReportPropertyRelevant(entity, key, anchor)) {
				return results;
			}

			const groupId = getSearchPropertyGroup(key, doc, entity);
			results.push({
				property: key,
				label: _.get(doc, "name", key),
				value: formatReportValue(value),
				defaultValue: hasDefault ? formatReportValue(defaultValue) : "",
				anchor,
				targetAnchor: getPropertyTargetAnchor(key, doc),
				groupId,
				panelLabel: getPanelLabelForAnchor(anchor)
			});
			return results;
		}, [])
		.sort((a, b) => `${a.panelLabel} ${a.label}`.localeCompare(`${b.panelLabel} ${b.label}`));
}
